import type { ComponentType, ReactNode } from 'react';

import { NewTabAnchor } from '@/components/ui/new-tab-hint';
import { cn } from '@/lib/utils';

export const CTA_BASE =
  'inline-flex w-full items-center justify-center gap-2 rounded-md bg-(--hotel-card-cta-bg,var(--brand-accent,var(--primary))) px-3 py-2 text-sm font-medium text-(--hotel-card-cta-fg,var(--brand-accent-foreground,var(--primary-foreground)))';

export const CTA_ENABLED =
  'cursor-pointer no-underline transition-opacity duration-150 hover:opacity-90';
/** How this surface says a control is inert, shared so a disabled chip and a disabled CTA agree. */
export const CONTROL_DISABLED = 'cursor-not-allowed opacity-40';

export type CardLinkComponent = ComponentType<{
  href: string;
  className?: string;
  children: ReactNode;
  onClick?: () => void;
  'aria-label'?: string;
  'aria-describedby'?: string;
}>;

/** A new-tab anchor: no analytics, no iframe handling. */
export const PlainLink: CardLinkComponent = NewTabAnchor;

/**
 * The hotel/room card call-to-action, in one place so the chat card, the room
 * card and the widget all render the same button.
 *
 * Kept next-intl-free and link-library-free so the MCP widget can import it. An
 * in-chat caller passes `linkComponent={MarkdownLink}` so outbound clicks keep
 * their link-location analytics; a widget passes nothing and gets a plain anchor,
 * which is also what keeps `MarkdownLink` out of that bundle.
 */
export function HotelCta({
  label,
  onClick,
  href,
  className,
  linkComponent: Link = PlainLink,
}: {
  label: ReactNode;
  /** Records the choice. On its own it navigates in place (the next room); alongside `href` it
   *  runs as the guest leaves, so what they picked is still on screen behind them. */
  onClick?: () => void;
  /** Opens the supplier site in a new tab. */
  href?: string;
  className?: string;
  /** Renders `href`. Without it, a plain anchor — no analytics, no iframe handling. */
  linkComponent?: CardLinkComponent;
}) {
  if (onClick && !href) {
    return (
      <button type="button" onClick={onClick} className={cn(CTA_BASE, CTA_ENABLED, className)}>
        {label}
      </button>
    );
  }

  // Without a role `aria-disabled` is ignored, and the label reads as plain text rather than as
  // the booking link it stands in for.
  if (!href) {
    return (
      <span role="link" aria-disabled="true" className={cn(CTA_BASE, CONTROL_DISABLED, className)}>
        {label}
      </span>
    );
  }

  return (
    <Link href={href} onClick={onClick} className={cn(CTA_BASE, CTA_ENABLED, className)}>
      {label}
    </Link>
  );
}
